import React from 'react'
import useCrispChat from 'app/Hooks/useCrispChat'
import 'bootstrap-icons/font/bootstrap-icons.css'
import Button from 'app/components/Button'
import CustomModal from './CustomModal'

interface UpgradeModalProps {
  show: boolean
  onClose: () => void
}

const UpgradePlan: React.FC<UpgradeModalProps> = ({show, onClose}) => {
  const {toggleCrispChat} = useCrispChat()

  if (!show) {
    return null
  }

  const reviews = [
    {
      name: 'Capterra',
      rating: 4.1,
      total: 5,
      icon: '/media/svg/capterra.svg',
    },
    {
      name: 'G2',
      rating: 4.5,
      total: 5,
      icon: '/media/svg/G2.svg',
    },
    {
      name: 'TrustRadius',
      rating: 4.4,
      total: 5,
      icon: '/media/svg/trustradius.svg',
    },
  ]

  const renderStars = (rating: number, total: number): React.ReactNode[] => {
    const stars: React.ReactNode[] = []
    const fullStars = Math.floor(rating)
    const hasHalfStar = rating % 1 !== 0

    // Render full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <span key={i} style={{color: '#F59E0B', fontSize: '18px', marginRight: '1px'}}>
          ★
        </span>
      )
    }

    // Render half star
    if (hasHalfStar) {
      stars.push(
        <span
          key='half'
          style={{
            fontSize: '18px',
            marginRight: '1px',
            position: 'relative',
            display: 'inline-block',
          }}
        >
          <span style={{color: '#FECACA', position: 'absolute'}}>★</span>{' '}
          {/* Faded background star */}
          <span style={{color: '#F59E0B', clipPath: 'inset(0 50% 0 0)'}}>★</span>{' '}
          {/* Filled half star */}
        </span>
      )
    }

    // Render remaining empty stars
    const remainingStars = total - Math.ceil(rating)
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <span key={`empty-${i}`} style={{color: '#FECACA', fontSize: '18px', marginRight: '1px'}}>
          ★
        </span>
      )
    }

    return stars
  }

  return (
    <CustomModal show={show} onClose={onClose}>
      <p className='fw-bold mb-5 fs-1 text-gray-900'>
        Get the most out of SecureSlate <br /> with a Premium Plan
      </p>
      <p className='text-gray-900 fs-7' style={{textAlign: 'left', marginBottom: '20px'}}>
        Join thousands of teams leveraging SecureSlate to streamline compliance, protect data, and
        grow with confidence.
      </p>

      <div className='d-flex justify-content-between gap-4 mb-4'>
        <div className='text-center'>
          <div className='fw-medium d-flex align-items-center'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='#0e9f6e'
              className='icon icon-tabler icons-tabler-filled icon-tabler-circle-check me-2'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-1.293 5.953a1 1 0 0 0 -1.32 -.083l-.094 .083l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.403 1.403l.083 .094l2 2l.094 .083a1 1 0 0 0 1.226 0l.094 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z' />
            </svg>
            180+ Integrations
          </div>
        </div>
        <div className='text-center'>
          <div className='fw-medium d-flex align-items-center'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='#0e9f6e'
              className='icon icon-tabler icons-tabler-filled icon-tabler-circle-check me-2'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-1.293 5.953a1 1 0 0 0 -1.32 -.083l-.094 .083l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.403 1.403l.083 .094l2 2l.094 .083a1 1 0 0 0 1.226 0l.094 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z' />
            </svg>
            Save $20K+
          </div>
        </div>
        <div className='text-center'>
          <div className='fw-medium d-flex align-items-center'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='#0e9f6e'
              className='icon icon-tabler icons-tabler-filled icon-tabler-circle-check me-2'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-1.293 5.953a1 1 0 0 0 -1.32 -.083l-.094 .083l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.403 1.403l.083 .094l2 2l.094 .083a1 1 0 0 0 1.226 0l.094 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z' />
            </svg>
            Save 80+ hours a month
          </div>
        </div>
      </div>
      <div
        style={{
          background: 'linear-gradient(129deg, #FFFFFF 0%, #E0FFF2 50%, #E0FFF2 100%)',
          padding: '35px 30px',
          borderRadius: '16px',
          maxWidth: '520px',
          border: '0.5px solid #D1D5DB',
          margin: '25px auto',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px',
            gap: '20px',
          }}
        >
          {reviews.map((review, index) => (
            <div
              key={index}
              style={{
                textAlign: 'center',
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  marginBottom: '8px',
                }}
              >
                <img
                  src={review.icon}
                  alt={`${review.name} logo`}
                  // style={{ width: '40px', height: '30px' }}
                />
              </div>

              <div
                style={{
                  marginBottom: '12px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {renderStars(review.rating, review.total)}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#166534',
                  fontWeight: '700',
                  letterSpacing: '-0.01em',
                  border: '2px solid #bbf7d0',
                  padding: '1px 6px',
                  borderRadius: '6px',
                  backgroundColor: '#dcfce7',
                }}
              >
                {review.rating}/5
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            textAlign: 'center',
            marginTop: '25px',
          }}
        >
          <a
            href='https://secureslate.medium.com/list/case-studies-5f4bb77fefff'
            style={{
              fontSize: '14px',
              textDecoration: 'underline',
              fontWeight: '500',
              letterSpacing: '-0.01em',
              textDecorationColor: '#9CA3AF',
              textUnderlineOffset: '2px',
            }}
            onMouseOver={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.textDecorationColor = '#374151'
            }}
            onMouseOut={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.textDecorationColor = '#9CA3AF'
            }}
            className='text-gray-900 d-flex align-items-center justify-content-center gap-1'
          >
            Read customer case stories{' '}
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              stroke-width='2'
              stroke-linecap='round'
              stroke-linejoin='round'
              className='icon icon-tabler icons-tabler-outline icon-tabler-external-link'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6' />
              <path d='M11 13l9 -9' />
              <path d='M15 4h5v5' />
            </svg>
          </a>
        </div>
      </div>
      <div className='d-flex justify-content-center mb-3'>
        <Button onClick={toggleCrispChat} btnType='primary' style={{width: '90%'}}>
          Contact Sales
        </Button>
      </div>

      <p className='small text-center text-gray-900 fs-7'>
        Risk-free for 30 days. 100% money-back guarantee.
      </p>
    </CustomModal>
  )
}

export default UpgradePlan
